/**
 * InitializationHistoryModal
 *
 * Read-only view of the ferris-wheel snapshot for a given rodeo —
 * shows prior → new positions per member captured at initialization.
 * Used in two places:
 *   - Rodeo card header on the Manage Draws list (top-level access)
 *   - InitializeDrawPanel on the per-event drilldown page
 *
 * Lazily fetches the snapshot only when `open` flips to true so
 * pages with many rodeo cards don't fan out an N×snapshot request
 * on first paint.
 */

import { History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useInitializationSnapshot } from '@/api/extensions/useDrawInitialization';

interface InitializationHistoryModalProps {
  open: boolean;
  onClose: () => void;
  rodeoId: string;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso ?? '—';
  }
}

export function InitializationHistoryModal({
  open,
  onClose,
  rodeoId,
}: InitializationHistoryModalProps) {
  // Only fetch once the modal opens — saves a wasted round-trip on every
  // page load for rodeos whose history nobody ever opens.
  const { data, isLoading } = useInitializationSnapshot(open ? rodeoId : null);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Flex column with explicit height so the inner table scrolls
          INSIDE the modal instead of overflowing past the viewport.
          `min-h-0` on flex children is required for nested scroll. */}
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-3xl">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Draw initialization history
          </DialogTitle>
          <DialogDescription>
            Prior → new positions captured when this rodeo was flipped
            from sandbox to live. Manual position edits made afterwards
            are not shown here.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2 py-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : !data ? (
          <p className="py-6 text-sm text-muted-foreground">
            No initialization snapshot exists for this rodeo yet.
          </p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="shrink-0 grid grid-cols-2 gap-2 rounded border border-border bg-muted/30 p-3 text-xs sm:grid-cols-4">
              <div>
                <div className="text-muted-foreground">Initialized</div>
                <div className="font-medium">{formatDate(data.executed_at)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Members</div>
                <div className="font-medium">{data.member_count}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Random seed</div>
                <div className="truncate font-mono">{data.random_seed}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Group sizes</div>
                <div className="font-medium">{data.group_sizes.join(', ')}</div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto rounded border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-muted text-xs uppercase text-muted-foreground shadow-sm">
                  <tr>
                    {/* Leading row-index column purely as a scroll
                        anchor — lets the user see "row 73 of 197"
                        without counting. Distinct from new_position
                        which is the assigned ferris-wheel slot. */}
                    <th className="px-3 py-2 text-right font-semibold w-12">#</th>
                    <th className="px-3 py-2 text-left font-semibold">Member</th>
                    <th className="px-3 py-2 text-right font-semibold">Prior pos</th>
                    <th className="px-3 py-2 text-right font-semibold">Prior grp</th>
                    <th className="px-3 py-2 text-right font-semibold">New pos</th>
                    <th className="px-3 py-2 text-right font-semibold">New grp</th>
                  </tr>
                </thead>
                <tbody>
                  {/* An association with no memberships initializes to an
                      empty rotation — show why the table is blank instead
                      of just headers. */}
                  {data.changes.length === 0 ? (
                    <tr className="border-t border-border">
                      <td
                        colSpan={6}
                        className="px-3 py-6 text-center text-muted-foreground"
                      >
                        No members were on the priority list, so the ferris
                        wheel rotated nobody.
                      </td>
                    </tr>
                  ) : null}
                  {data.changes.map((c, idx) => (
                    <tr
                      key={c.id}
                      className="border-t border-border odd:bg-background even:bg-muted/20"
                    >
                      <td className="px-3 py-1.5 text-right text-xs tabular-nums text-muted-foreground">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-1.5">
                        {/* "Display Name (M-1234)" — name first so the
                            table is scannable by person, card number
                            in parens as the secondary identifier. */}
                        {c.display_name ? (
                          <>
                            <span className="font-medium">{c.display_name}</span>
                            {c.card_number ? (
                              <span className="ml-1 font-mono text-xs text-muted-foreground">
                                ({c.card_number})
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="font-mono text-xs">
                            {c.card_number ?? '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {c.prior_position ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {c.prior_group ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-semibold">
                        {c.new_position}
                      </td>
                      <td className="px-3 py-1.5 text-right font-semibold">
                        {c.new_group}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
